import type { PagoDeCuenta } from '@/lib/supabase/tipos-bd'
import { fecha, pesos } from '@/shared/utilidades/formato'

import { usePagosDeCuenta, useQuitarPagoRechazado } from './useCartera'
import estilos from './PaginaCartera.module.css'

/**
 * Los pagos de una cuenta: cuándo, cuánto, con qué documento y a qué factura.
 * Es lo primero que se pregunta cuando un cliente dice «yo ya pagué».
 *
 * Desde la 0293 salen de World Office: cada recibo de caja, nota crédito o
 * devolución que abona una FEV de la obra, y las FEV de contado. Lo anotado
 * en la app que WO todavía no refleja sale «en camino»; si WO lo rechazó, sale
 * en rojo con el motivo y se puede quitar.
 */
const MEDIO: Record<string, string> = {
  transferencia: 'Transferencia',
  consignacion: 'Consignación',
  efectivo: 'Efectivo',
  tarjeta: 'Tarjeta',
  cheque: 'Cheque',
  otro: 'Otro',
  // Abona la factura con un anticipo que el cliente ya había pagado (0294).
  anticipo: 'Cruce de anticipo',
}

const TIPO: Record<PagoDeCuenta['tipo'], string> = {
  recibo: 'Recibo de caja',
  nota_credito: 'Nota crédito',
  devolucion: 'Devolución',
  nota_contable: 'Nota de contabilidad',
  nota_debito: 'Nota débito',
  contado: 'De contado',
  pago_app: 'Anotado en la app',
}

export function PagosDeCuenta({ ordenServicioId }: { ordenServicioId: string }) {
  const { data: pagos = [], isLoading, error } = usePagosDeCuenta(ordenServicioId, true)

  if (isLoading) return <p className={estilos.pagosVacio}>Buscando los pagos…</p>
  if (error) return <p className={estilos.error}>{(error as Error).message}</p>
  if (pagos.length === 0) {
    return (
      <p className={estilos.pagosVacio}>
        Ni World Office ni la app tienen pagos de esta obra todavía.
      </p>
    )
  }

  return (
    <ol className={estilos.pagos}>
      {pagos.map((p) => (
        <Pago key={p.id} pago={p} />
      ))}
    </ol>
  )
}

function Pago({ pago: p }: { pago: PagoDeCuenta }) {
  const quitar = useQuitarPagoRechazado()
  const estado =
    p.estado === 'error'
      ? estilos.pagoRechazado
      : p.estado === 'en_camino'
      ? estilos.pagoEnCamino
      : ''
  // De WO llega el usuario contable; solo se nombra a quien lo anotó en la app.
  const quien = p.quien && p.origen === 'app' ? p.quien.split(' ').slice(0, 2).join(' ') : null

  return (
    <li className={`${estilos.pago} ${estado}`}>
      <span className={estilos.pagoPunto} aria-hidden="true" />
      <div className={estilos.pagoTexto}>
        <p className={estilos.pagoTope}>
          <b>{pesos(Number(p.valor))}</b>
          <span>{fecha(p.fecha)}</span>
          {p.estado === 'en_camino' && <span className={estilos.pagoChip}>en camino a WO</span>}
          {p.estado === 'error' && <span className={estilos.pagoChip}>WO lo rechazó</span>}
        </p>
        <p className={estilos.pagoMeta}>
          {p.tipo === 'pago_app' && p.documento ? 'Recibo de caja' : TIPO[p.tipo] ?? p.tipo}
          {p.documento && p.tipo !== 'contado' && <> {p.documento}</>}
          {p.factura && <> · a la {p.factura}</>}
          {p.medio && p.tipo !== 'contado' && !(p.origen === 'wo' && p.medio === 'otro') && (
            <> · {MEDIO[p.medio] ?? p.medio}</>
          )}
          {p.referencia && <> · {p.referencia}</>}
          {quien && <> · anotó {quien}</>}
        </p>
        {p.estado === 'error' ? (
          <p className={estilos.pagoNota}>
            {p.error ?? 'World Office no lo aceptó.'} No cuenta en el saldo.{' '}
            <button
              type="button"
              className={estilos.pagoQuitar}
              onClick={() => quitar.mutate(p.id)}
              disabled={quitar.isPending}
            >
              {quitar.isPending ? 'Quitando…' : 'Quitarlo'}
            </button>
          </p>
        ) : (
          p.nota &&
          p.tipo !== 'contado' && <p className={estilos.pagoNota}>{p.nota}</p>
        )}
        {quitar.error && <p className={estilos.error}>{quitar.error.message}</p>}
      </div>
    </li>
  )
}
